import * as React from "react";
import { Link } from "react-ui";

import { isChildOfType, nullElement } from "../../TypingUtils";
import { AbsoluteModelPath, AbsoluteResolvedModelPath } from "../../../../../Common/ModelPath/ModelPath";
import { uuid } from "../../../../../Common/UUID";
import { TestPageObjectHandle } from "../../Services/TestPageObjects/TestPageObjectHandle";
import { FocusManagementObserver } from "../../Services/FocusManagement/FocusManagementObserver";
import { BindingPath } from "../../../Engine/Core/CoreTypes";
import { ExecuteFormActionHandler } from "../../Internals/FormActionHandlers/FormActionHandler";
import { Icon } from "../../Icons/Icon";

import { ModalFormContainer, ModalFormContainerProps } from "./ModalFormContainer";
import { useModalFormsRegistry } from "./ModalFormsContext";

export interface ModalFormLabelProps {
    bindingPath: AbsoluteResolvedModelPath;
    navigationPathMask?: BindingPath;
    relativeNavigationPath?: AbsoluteModelPath;
    children: React.ReactNode;
    modalId?: string;
    executeFormActionHandler: ExecuteFormActionHandler;
    isReadOnly?: boolean;
    showDefaultReadOnlyButton?: boolean;
}

export const ModalFormLabel = (props: ModalFormLabelProps): JSX.Element => {
    const modalFormsRegistry = useModalFormsRegistry();

    let modalId = "";
    let modal: React.ReactElement<React.PropsWithChildren<ModalFormContainerProps>> | null = nullElement;
    const children: React.ReactNode[] = [];

    React.Children.forEach(props.children ?? [], child => {
        if (!isChildOfType(child, ModalFormContainer)) {
            children.push(child);
        }
    });

    const handleOpen = async () => {
        modalFormsRegistry.openModal(modalId);
    };
    function handleClose() {
        modalFormsRegistry.closeModal(modalId);
    }

    const hasInternalButton = children.length > 0;

    modalId = React.useMemo(() => {
        if (props.modalId != undefined) {
            return props.modalId;
        }
        if (!hasInternalButton) {
            return props.bindingPath.toLegacyPath();
        }
        return uuid();
    }, [props.modalId, hasInternalButton]);

    const handleFocus = (path: unknown) => {
        if (typeof path !== "string") {
            return;
        }

        if (props.bindingPath.toLegacyPath() === path) {
            modalFormsRegistry.openModal(modalId);
        } else if (props.relativeNavigationPath) {
            const lastToken = props.relativeNavigationPath.getPathPartsAsArray().slice(-1)[0];

            if (!lastToken || typeof lastToken !== "string") {
                return;
            }

            const absoluteNavigationPath = props.bindingPath.joinWith(lastToken);
            if (path.startsWith(absoluteNavigationPath.toLegacyPath())) {
                modalFormsRegistry.openModal(modalId);
            }
        } else {
            modalFormsRegistry.openModal(path);
        }
    };

    const readonlyLabel = (
        <span>
            <Link icon={<Icon name="EyeOpenIcon" size={16} weight="light" />} onClick={handleOpen}>
                Посмотреть данные
            </Link>
        </span>
    );

    React.Children.forEach(props.children ?? [], child => {
        if (isChildOfType(child, ModalFormContainer)) {
            modal = React.cloneElement(child as React.ReactElement<React.PropsWithChildren<ModalFormContainerProps>>, {
                handleClose: handleClose,
                modalRegistry: modalFormsRegistry,
                modalId: modalId,
            });
        }
    });

    React.useEffect(() => {
        if (modal?.props?.onAfterOpen != undefined) {
            return modalFormsRegistry.subscribe("afterOpen", modalId, async () => {
                if (modal?.props?.onAfterOpen != undefined) {
                    await props.executeFormActionHandler(modal.props.onAfterOpen);
                }
            });
        }

        return undefined;
    }, [modalId]);

    return (
        <TestPageObjectHandle
            id={"ModalForm"}
            pageObject={{
                getType: () => `ModalForm_${modalId}`,
                clickOpen: () => handleOpen(),
                clickClose: () => handleClose(),
            }}>
            <FocusManagementObserver onRequestFocus={handleFocus}>
                <span>
                    {props.isReadOnly && props.showDefaultReadOnlyButton ? (
                        readonlyLabel
                    ) : (
                        <span onClick={handleOpen}>{children}</span>
                    )}
                    {modalFormsRegistry.isOpened(modalId) ? modal : nullElement}
                </span>
            </FocusManagementObserver>
        </TestPageObjectHandle>
    );
};
